import { Button } from '@goodboydigital/astro';
import gsap from 'gsap';
import { Rectangle, Sprite } from 'pixi.js';

import { Shared } from '../../App';
import LocalisedText from '../LocalisedText';
import { LocalisedTextAnimated } from '../LocalisedTextAnimated';
import { Panel } from '../Panel';
import { RectangleButton } from './RectangleButton';

const defaultOpts = {
    w: 200,
    h: 60,
    scale: 1,
    alpha: 1,
    accessible: false,
    accessibleTitle: 'Select Button',
    titleText: 'Select Button',
    buttonText: 'CHOOSE',
    buttonTextOffset: 0,
    tabIndex: 0,
    buttonAsset: 'button-ninesliced-yellow',
    characterSprite: new Sprite(),
    nineSliceOffset: 50,
    itemHolderAsset: 'item-holder-large',
};

export type RewardButtonOpts = typeof defaultOpts;

export class SelectPanelButton extends Button
{
    public view: Sprite;
    public titleText: LocalisedText;
    public originalX: number;
    public offsetX: number;
    public panelTarget: Panel | undefined;

    protected btn: RectangleButton;

    constructor(opts: Partial<RewardButtonOpts> = {})
    {
        const o = { ...defaultOpts, ...opts };

        const view = new Sprite();

        view.anchor.set(0.5);
        view.scale.set(o.scale);
        view.alpha = o.alpha;

        super(view, o);
        this.view = view;

        const itemHolder = o.itemHolderAsset
            ? Sprite.from(Shared.assets.getTexture(o.itemHolderAsset))
            : new Sprite();

        itemHolder.anchor.set(0.5);
        itemHolder.scale.set(1.8);
        itemHolder.addChild(o.characterSprite);
        itemHolder.y -= 10;

        view.addChild(itemHolder);

        this.titleText = new LocalisedTextAnimated(o.titleText, {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            stroke: 0x333333,
            strokeThickness: 5,
            lineJoin: 'round',
            align: 'center',
            fontSize: 36,
            maxWidth: 270,
        });

        this.titleText.y -= 155;
        this.titleText.anchor.set(0.5);
        view.addChild(this.titleText);

        this.btn = new RectangleButton({
            text: o.buttonText,
            w: o.w,
            h: o.h,
            fontSize: 36,
            wobble: true,
            stroke: 6,
        });
        this.btn.view.y += 135;

        this.view.addChild(this.btn.view);

        const hitW = 350;
        const hitH = 400;

        this.view.hitArea = new Rectangle(
            -hitW / 2,
            -hitH / 2,
            hitW,
            hitH,
        );

        this.originalX = this.view.x;
        this.offsetX = this.view.x;
    }

    public down(): void
    {
        this.btn.down();

        if (!this.panelTarget) return;

        gsap.killTweensOf(this.panelTarget.view);
        gsap.to(this.panelTarget.view, {
            duration: 0.8,
            x: this.offsetX,
            ease: 'elastic.out(1.6, 0.3)',
        });
    }

    public up(): void
    {
        this.btn.up();

        if (!this.panelTarget) return;

        gsap.killTweensOf(this.panelTarget.view);
        gsap.to(this.panelTarget.view, {
            duration: 0.8,
            x: this.originalX,
            ease: 'elastic.out(1.6, 0.3)',
        });
    }

    public hover(): void
    {
        this.btn.hover();

        if (!this.panelTarget) return;

        gsap.killTweensOf(this.panelTarget.view);
        gsap.to(this.panelTarget.view, {
            duration: 0.8,
            x: this.offsetX,
            ease: 'elastic.out(1.6, 0.3)',
        });
    }
}
